import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";
import { RESOURCES, RESOURCE_LIST } from "../data/resources";
import { api } from "../lib/api";

export const ResourcesIndex = () => (
  <Layout title="Free Resources & Lead Magnets | The Authority Gap" path="/resources">
    <section style={{ background: "#090909", padding: "70px 32px 36px", position: "relative", overflow: "hidden", textAlign: "center" }}>
      <HeroBg />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto" }}>
        <div style={{ marginBottom: 22 }}><EyebrowPill>Resources</EyebrowPill></div>
        <h1 className="tag-h1" style={{ marginBottom: 12 }}>Free resources.</h1>
        <p style={{ color: "#aaa", fontSize: 15 }}>Six guides, blueprints, and toolkits that walk you through AuthorityOS in detail. Email-gated.</p>
      </div>
    </section>
    <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "40px 20px 64px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }} className="tag-res-grid">
        {RESOURCE_LIST.map((r) => (
          <Link key={r.slug} to={`/resources/${r.slug}`} data-testid={`resource-card-${r.slug}`} className="tag-lab-card" style={{ textDecoration: "none", display: "block" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: r.accent }} />
            <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 8 }}>
              {r.type} · {r.pages} pages
            </div>
            <h3 style={{ color: r.accent, fontSize: 15, fontWeight: 700, margin: "0 0 8px" }}>{r.name}</h3>
            <p style={{ color: "#bbb", fontSize: 11, lineHeight: 1.6, margin: 0 }}>{r.blurb}</p>
            <div style={{ marginTop: 14, color: "#FF6575", fontSize: 12, fontWeight: 600 }}>Get free →</div>
          </Link>
        ))}
      </div>
      <style>{`@media (max-width:880px){ .tag-res-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </section>
  </Layout>
);

export default function ResourcePage() {
  const { slug } = useParams();
  const r = RESOURCES[slug];
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!r) return <Navigate to="/resources" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email.");
    setSubmitting(true);
    try {
      const res = await api.post("/resources/request", { slug, email });
      setDownloadUrl(res.data?.download_url || "");
      setDone(true);
    } catch {
      toast.error("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title={`${r.name} | The Authority Gap`} path={`/resources/${slug}`}>
      <section style={{ background: "#090909", padding: "60px 20px 32px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto" }}>
          <div style={{ marginBottom: 18, color: "#888", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
            {r.type} · {r.pages} pages · Free
          </div>
          <h1 className="tag-h1" style={{ marginBottom: 12, fontSize: 36 }}>{r.name}</h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>{r.blurb}</p>
        </div>
      </section>
      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "32px 20px 64px" }}>
        <div className="tag-card" data-testid="resource-gate" style={{ maxWidth: 460, margin: "0 auto", padding: 28 }}>
          {!done ? (
            <form onSubmit={submit}>
              <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Where should we send it?</h3>
              <p style={{ color: "#888", fontSize: 11, margin: "0 0 16px" }}>You'll also get the Authority Gap email series. Unsubscribe any time.</p>
              <input
                data-testid="resource-email"
                type="email"
                className="tag-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              <button data-testid="resource-submit" type="submit" className="btn-primary" style={{ width: "100%" }} disabled={submitting}>
                {submitting ? "Sending…" : `Send me the ${r.type}`}
              </button>
            </form>
          ) : (
            <div data-testid="resource-success" style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ color: "#FF6575", fontSize: 48, marginBottom: 8 }}>✓</div>
              <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, margin: "0 0 8px" }}>It's on the way.</h3>
              <p style={{ color: "#bbb", fontSize: 13, marginBottom: 18 }}>
                {downloadUrl
                  ? <>We just emailed your copy to <span style={{ color: "#fff" }}>{email}</span>. Or download it now:</>
                  : <>Check {email} in a few minutes. While you wait — take the free <Link to="/score" style={{ color: "#FF6575" }}>Authority Score</Link>.</>}
              </p>
              {downloadUrl && (
                <a
                  data-testid="resource-download"
                  href={downloadUrl}
                  className="btn-primary"
                  style={{ display: "inline-block", marginBottom: 12 }}
                >
                  Download {r.type}
                </a>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
